import { t as translate } from "./preferences";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import { canManageStock, canSell, hasAnyBusinessCapability, hasCapability, useAuth } from "./auth";
import { AppShell } from "./components/AppShell";
import {
  BootScreen,
  ErrorScreen,
  NotFoundScreen,
} from "./components/ScreenState";
import { APP_ROUTES, type AppRoute } from "./routes";
import { ChangePasswordScreen } from "./screens/ChangePasswordScreen";
import { ClientDetailScreen } from "./screens/ClientDetailScreen";
import { ClientFormScreen } from "./screens/ClientFormScreen";
import { ClientsScreen } from "./screens/ClientsScreen";
import { HistoryScreen } from "./screens/HistoryScreen";
import { HomeScreen } from "./screens/HomeScreen";
import { LoginScreen } from "./screens/LoginScreen";
import { ProfileEditScreen } from "./screens/ProfileEditScreen";
import { ProfilePasswordScreen } from "./screens/ProfilePasswordScreen";
import { ProfileScreen } from "./screens/ProfileScreen";
import { RestrictedScreen } from "./screens/RestrictedScreen";
import { StatisticsScreen } from "./screens/StatisticsScreen";
import { VehicleDetailScreen } from "./screens/VehicleDetailScreen";
import { VehicleFormScreen } from "./screens/VehicleFormScreen";
import { VehiclesScreen } from "./screens/VehiclesScreen";
import { WorkScreen } from "./screens/WorkScreen";
import { WorkConfirmationScreen } from "./screens/WorkConfirmationScreen";
import {
  JarayondaDetailScreen,
  JarayondaListScreen,
  JarayondaReserveScreen,
  StockDocumentScreen,
  WarehouseScreen,
  WriteOffScreen,
} from "./screens/WarehouseScreens";
import { ReturnListScreen, ReturnDetailScreen } from "./screens/ReturnScreens";
import type { UserOut } from "./api";
import { SupportProvider, SupportListScreen, SupportDetailScreen, SupportCreateScreen } from "./support";
import { usePreferences } from "./preferences";
import { useRef } from "react";

export function routeAllowed(user: UserOut, route: AppRoute): boolean {
  if (route.capability === "account") return true;
  if (route.capability === "general") return hasAnyBusinessCapability(user);
  return hasCapability(user, route.capability);
}

export function AuthenticatedApp({ user }: { user: UserOut }) {
  const { access } = useAuth();
  const location = useLocation();
  const zeroCapability = !hasAnyBusinessCapability(user);
  const warehouseLocked = access?.tariff && !access.tariff.features.includes("tovars");
  const allowedRoutes = APP_ROUTES.filter((route) => routeAllowed(user, route) && !(route.capability === "warehouse" && warehouseLocked));
  const businessBlocked = !location.pathname.startsWith("/mechanic/profile") && !location.pathname.startsWith("/mechanic/support") && (user.workshop_expired || access?.tariff?.is_expired || (location.pathname.startsWith("/mechanic/warehouse") && warehouseLocked));

  return (
    <SupportProvider><AppShell allowedRoutes={allowedRoutes}>
      {businessBlocked ? <RestrictedScreen zeroCapability={zeroCapability} /> : null}
      <div hidden={Boolean(businessBlocked)}>
      <Routes>
        <Route path="/mechanic/" element={zeroCapability ? <RestrictedScreen zeroCapability /> : <HomeScreen />} />
        <Route path="/mechanic/history" element={zeroCapability ? <RestrictedScreen zeroCapability /> : <HistoryScreen />} />
        <Route path="/mechanic/statistics" element={zeroCapability ? <RestrictedScreen zeroCapability /> : <StatisticsScreen />} />
        <Route path="/mechanic/clients" element={zeroCapability ? <RestrictedScreen zeroCapability /> : <ClientsScreen />} />
        <Route path="/mechanic/clients/new" element={zeroCapability ? <RestrictedScreen zeroCapability /> : <ClientFormScreen mode="new" />} />
        <Route path="/mechanic/clients/:clientId" element={zeroCapability ? <RestrictedScreen zeroCapability /> : <ClientDetailScreen />} />
        <Route path="/mechanic/clients/:clientId/edit" element={zeroCapability ? <RestrictedScreen zeroCapability /> : <ClientFormScreen mode="edit" />} />
        <Route path="/mechanic/clients/:clientId/vehicles/new" element={zeroCapability ? <RestrictedScreen zeroCapability /> : <VehicleFormScreen mode="new" />} />
        <Route path="/mechanic/vehicles" element={zeroCapability ? <RestrictedScreen zeroCapability /> : <VehiclesScreen />} />
        <Route path="/mechanic/vehicles/:vehicleId" element={zeroCapability ? <RestrictedScreen zeroCapability /> : <VehicleDetailScreen />} />
        <Route path="/mechanic/vehicles/:vehicleId/edit" element={zeroCapability ? <RestrictedScreen zeroCapability /> : <VehicleFormScreen mode="edit" />} />
        <Route path="/mechanic/profile" element={<ProfileScreen />} />
        <Route path="/mechanic/profile/edit" element={<ProfileEditScreen />} />
        <Route path="/mechanic/profile/password" element={<ProfilePasswordScreen />} />
        <Route path="/mechanic/work" element={hasCapability(user, "usta") ? <WorkScreen /> : <RestrictedScreen zeroCapability={zeroCapability} />} />
        <Route path="/mechanic/work/confirmation" element={hasCapability(user, "usta") ? <WorkConfirmationScreen /> : <RestrictedScreen zeroCapability={zeroCapability} />} />
        <Route path="/mechanic/warehouse" element={hasCapability(user, "warehouse") ? <WarehouseScreen /> : <RestrictedScreen zeroCapability={zeroCapability} />} />
        <Route path="/mechanic/warehouse/purchase" element={canManageStock(user) ? <StockDocumentScreen mode="purchase" /> : <RestrictedScreen zeroCapability={zeroCapability} />} />
        <Route path="/mechanic/warehouse/sale" element={canSell(user) ? <StockDocumentScreen mode="sale" /> : <RestrictedScreen zeroCapability={zeroCapability} />} />
        <Route path="/mechanic/warehouse/purchase-return" element={canManageStock(user) ? <ReturnListScreen mode="purchase" /> : <RestrictedScreen zeroCapability={zeroCapability} />} />
        <Route path="/mechanic/warehouse/sale-return" element={canSell(user) ? <ReturnListScreen mode="sale" /> : <RestrictedScreen zeroCapability={zeroCapability} />} />
        <Route path="/mechanic/warehouse/purchase-return/:documentId" element={canManageStock(user) ? <ReturnDetailScreen key={location.pathname} mode="purchase" /> : <RestrictedScreen zeroCapability={zeroCapability} />} />
        <Route path="/mechanic/warehouse/sale-return/:documentId" element={canSell(user) ? <ReturnDetailScreen key={location.pathname} mode="sale" /> : <RestrictedScreen zeroCapability={zeroCapability} />} />
        <Route path="/mechanic/warehouse/write-off" element={canManageStock(user) ? <WriteOffScreen /> : <RestrictedScreen zeroCapability={zeroCapability} />} />
        <Route path="/mechanic/warehouse/jarayonda" element={canSell(user) ? <JarayondaListScreen /> : <RestrictedScreen zeroCapability={zeroCapability} />} />
        <Route path="/mechanic/warehouse/jarayonda/new" element={canSell(user) ? <JarayondaReserveScreen /> : <RestrictedScreen zeroCapability={zeroCapability} />} />
        <Route path="/mechanic/warehouse/jarayonda/:saleId" element={canSell(user) ? <JarayondaDetailScreen /> : <RestrictedScreen zeroCapability={zeroCapability} />} />
        <Route path="/mechanic/support" element={<SupportListScreen />} />
        <Route path="/mechanic/support/new" element={<SupportCreateScreen />} />
        <Route path="/mechanic/support/:ticketId" element={<SupportDetailScreen />} />
        <Route path="/mechanic/restricted" element={<RestrictedScreen zeroCapability={zeroCapability} />} />
        <Route path="/mechanic/login" element={<Navigate to={zeroCapability ? "/mechanic/restricted" : "/mechanic/"} replace />} />
        <Route path="/mechanic/change-password" element={<Navigate to="/mechanic/profile/password" replace />} />
        <Route path="*" element={<NotFoundScreen />} />
      </Routes>
      </div>
    </AppShell></SupportProvider>
  );
}

export function MechanicApp() {
  usePreferences();
  const hadUnlockedSession = useRef(false);
  const { state, access, refresh } = useAuth();
  if (state.status === "authenticated" && !state.user.must_change_password) hadUnlockedSession.current = true;

  if (state.status === "booting") return <BootScreen />;
  if (state.status === "error") {
    return (
      <ErrorScreen
        offline={state.error.status === 0 || navigator.onLine === false}
        message={state.error.message}
        onRetry={state.retry}
      />
    );
  }
  if (state.status === "unauthenticated") {
    return (
      <Routes>
        <Route path="/mechanic/login" element={<LoginScreen />} />
        <Route path="*" element={<Navigate to="/mechanic/login" replace />} />
      </Routes>
    );
  }
  const recovering = access?.recovery;
  const password = state.user.must_change_password;
  return <>
    <div hidden={recovering || password} aria-hidden={recovering || password}>
      {access?.error && !recovering ? <div className="m-error-box m-m-3" role="alert">{access.error.message}<button className="m-secondary-button m-mt-2" onClick={() => void refresh?.()}>{translate("Qayta urinish")}</button></div> : null}
      <div {...{ inert: recovering || password || access?.checking ? "" : undefined }}>
        <AuthenticatedApp key={`${state.user.id}:${state.user.workshop_id}`} user={state.user} />
      </div>
    </div>
    {recovering ? <LoginScreen recovery /> : password ? <ChangePasswordScreen preserveRoute={hadUnlockedSession.current} /> : null}
  </>;
}
